import React, { useState, useEffect } from 'react';
import { Search, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import axios from 'axios';
import type { Participant } from '../../services/messagingService';
import type { ConversationType } from '../../services/messagingService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

type UserType = 'client' | 'professional';

interface NewConversationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectUser: (userId: string, conversationType: ConversationType) => void;
  isLoading?: boolean;
}

export function NewConversationModal({
  open,
  onOpenChange,
  onSelectUser,
  isLoading = false,
}: NewConversationModalProps) {
  const [userType, setUserType] = useState<UserType>('client');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<Participant[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Fetch users when modal opens or user type changes
  useEffect(() => {
    if (open) {
      loadUsers();
    } else {
      // Reset search when modal closes
      setSearchQuery('');
      setUserType('client');
    }
  }, [open, userType]);

  // Debounced search
  useEffect(() => {
    if (!open) return;

    const timeoutId = setTimeout(() => {
      loadUsers(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, open, userType]);

  const loadUsers = async (search?: string) => {
    setIsLoadingUsers(true);
    try {
      const endpoint = userType === 'client' ? 'clients' : 'professionals';
      const response = await axios.get(`${API}/admin/${endpoint}`, {
        params: search ? { search } : {},
      });
      setUsers(response.data);
    } catch (error) {
      console.error(`Error loading ${userType}s:`, error);
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleSelectUser = (userId: string) => {
    const conversationType: ConversationType = userType === 'client' ? 'CLIENT_ADMIN' : 'ADMIN_PRO';
    onSelectUser(userId, conversationType);
    onOpenChange(false);
    setSearchQuery('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Nouvelle conversation</DialogTitle>
          <DialogDescription>
            Sélectionnez un utilisateur pour démarrer une nouvelle conversation
          </DialogDescription>
        </DialogHeader>

        {/* Toggle Client | Professionnel */}
        <div className="flex gap-2 mb-4 w-full justify-center">
          <Button
            onClick={() => setUserType('client')}
            className={`flex-1 max-w-[200px] ${
              userType === 'client'
                ? 'bg-[#FF9F68] hover:bg-[#E67E4F] text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
            size="sm"
          >
            Clients
          </Button>
          <Button
            onClick={() => setUserType('professional')}
            className={`flex-1 max-w-[200px] ${
              userType === 'professional'
                ? 'bg-[#FF9F68] hover:bg-[#E67E4F] text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
            size="sm"
          >
            Professionnels
          </Button>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder={`Rechercher un ${userType === 'client' ? 'client' : 'professionnel'} par nom ou email...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Users List */}
        <div className="flex-1 overflow-y-auto mt-4 border rounded-lg">
          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF9F68]"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <User className="h-12 w-12 text-gray-300 mb-4" />
              <p className="text-gray-500 text-sm">
                {searchQuery
                  ? `Aucun ${userType === 'client' ? 'client' : 'professionnel'} trouvé`
                  : `Aucun ${userType === 'client' ? 'client' : 'professionnel'} disponible`}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {users.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleSelectUser(user.id)}
                  disabled={isLoading}
                  className="w-full p-4 text-left hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate mb-1">
                        {user.firstName} {user.lastName}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NewConversationModal;

